import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BrandLogo from "../../components/common/BrandLogo";
import Button from "../../components/common/Button";
import LogoUploadField from "../../components/common/LogoUploadField";
import { analyzeWebsite, analyzePrompt, completeOnboarding, AnalysisResult } from "../../api/onboarding";
import { useAppSelector } from "../../store";
import "../../styles/auth.scss"; // Reuse auth styles

const OnboardingPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const [step, setStep] = useState<"initial" | "website" | "prompt" | "review">("initial");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [websiteUrl, setWebsiteUrl] = useState("");
    const [promptText, setPromptText] = useState("");
    const [tempLogoPath, setTempLogoPath] = useState<string>("");


    // Determine initial company type based on user role
    const getInitialCompanyType = () => {
        // Check localStorage first (set during registration)
        const regType = localStorage.getItem("registration_company_type");
        if (regType === "agency") return "agency";
        if (regType === "individual" || regType === "company") return "single";

        // Fallback to role check
        if (user?.roles?.includes("agency_owner")) return "agency";
        return "single";
    };

    const [data, setData] = useState<AnalysisResult>({
        company: {
            company_name: "",
            company_type: getInitialCompanyType(),
            description: "",
            industry: "",
            website: "",
            email: "",
            phone: "",
            address: "",
            city: "",
            country: "",
            postal_code: "",
            tax_id: "",
            company_size: "",
            annual_revenue: 0,
            logo_url: "",
            establishment_date: ""
        },
        chatbot: {
            name: "My AI Assistant",
            description: "A helpful AI assistant for my company.",
            initial_message: "Hello! How can I help you today?"
        }
    });

    // Update company type if user loads later
    useEffect(() => {
        if (user) {
            setData(prev => ({
                ...prev,
                company: {
                    ...prev.company,
                    company_type: getInitialCompanyType()
                }
            }));
        }
    }, [user]);

    const handleWebsiteSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!websiteUrl) return;

        setIsLoading(true);
        setError(null);
        try {
            const result = await analyzeWebsite(websiteUrl);
            if (result && result.company) {
                setData({
                    company: {
                        ...result.company,
                        company_type: getInitialCompanyType() // Ensure type persists
                    },
                    chatbot: {
                        ...result.chatbot,
                        training_source: result.training_source // Map top-level to chatbot
                    }
                });
                setStep("review");
            } else {
                setError("Received empty or invalid response from server");
            }
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to analyze website");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePromptSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!promptText) return;

        setIsLoading(true);
        setError(null);
        try {
            const result = await analyzePrompt(promptText);
            setData({
                company: {
                    ...result.company,
                    company_type: getInitialCompanyType() // Ensure type persists
                },
                chatbot: {
                    ...result.chatbot,
                    training_source: result.training_source // Map top-level to chatbot
                }
            });
            setStep("review");
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to analyze prompt");
        } finally {
            setIsLoading(false);
        }
    };

    const [activeSection, setActiveSection] = useState<"company" | "contact">("company");

    const handleFinalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            // Add temp logo path to company data if exists
            const completionData = {
                ...data,
                company: {
                    ...data.company,
                    // Don't send data URL to backend - it's too long for VARCHAR(255)
                    // Only send temp_logo_path which will be migrated to permanent location
                    logo_url: data.company.logo_url?.startsWith('data:') ? '' : data.company.logo_url,
                    temp_logo_path: tempLogoPath || undefined
                }
            };
            const result = await completeOnboarding(completionData);
            const chatbotId = result.chatbot_id;

            // Verify company creation and prime cache
            try {
                // Import dynamically to avoid circular dependencies if any
                const { default: companiesService } = await import("../../features/companies/companiesService");
                await companiesService.getMyCompany();
            } catch (verifyError) {
                console.warn("Company verification failed, but proceeding:", verifyError);
            }

            // Clear the setup flag to prevent redirect loops
            localStorage.removeItem("companySetupRequired");

            // Redirect to the newly created chatbot in wizard mode
            // We force step=general and add onboarding=true flag
            if (chatbotId) {
                navigate(`/ai-chatbots/${chatbotId}/edit?step=general&onboarding=true`);
            } else {
                const isCompanyOwner = user?.roles?.includes("company_owner");
                navigate(isCompanyOwner ? "/ai-chatbots" : "/dashboard");
            }
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to complete onboarding");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (section: "company" | "chatbot", field: string, value: any) => {
        setData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const renderSidebar = () => (
        <div className="sidebar-content">

            <nav className="sidebar-nav">
                <button
                    type="button"
                    className={`sidebar-nav-item ${activeSection === "company" ? "active" : ""}`}
                    onClick={() => setActiveSection("company")}
                >
                    <div className="nav-item-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9,22 9,12 15,12 15,22"></polyline>
                        </svg>
                    </div>
                    <div className="nav-item-content">
                        <span className="nav-item-label">
                            {localStorage.getItem("registration_company_type") === "individual"
                                ? t("general_information")
                                : t("company_info")}
                        </span>
                        <span className="nav-item-step">Step 1</span>
                    </div>
                    {activeSection === "company" && (
                        <div className="nav-item-indicator">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="9,18 15,12 9,6"></polyline>
                            </svg>
                        </div>
                    )}
                </button>
                <button
                    type="button"
                    className={`sidebar-nav-item ${activeSection === "contact" ? "active" : ""}`}
                    onClick={() => setActiveSection("contact")}
                >
                    <div className="nav-item-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                    </div>
                    <div className="nav-item-content">
                        <span className="nav-item-label">Contact Details</span>
                        <span className="nav-item-step">Step 2</span>
                    </div>
                    {activeSection === "contact" && (
                        <div className="nav-item-indicator">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="9,18 15,12 9,6"></polyline>
                            </svg>
                        </div>
                    )}
                </button>


            </nav>
        </div>
    );

    // Firework celebration effect for welcome screen
    useEffect(() => {
        if (step === "initial") {
            createFireworks();
        }
    }, [step]);

    const createFireworks = () => {
        const container = document.querySelector('.firework-container');
        if (!container) return;

        const colors = ['purple', 'green', 'yellow'];
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 3;

        for (let i = 0; i < 30; i++) {
            setTimeout(() => {
                const firework = document.createElement('div');
                firework.className = `firework ${colors[Math.floor(Math.random() * colors.length)]}`;

                const angle = (Math.random() * 360) * (Math.PI / 180);
                const velocity = 50 + Math.random() * 150;
                const tx = Math.cos(angle) * velocity;
                const ty = Math.sin(angle) * velocity;

                firework.style.cssText = `
                    left: ${centerX}px;
                    top: ${centerY}px;
                    --tx: ${tx}px;
                    --ty: ${ty}px;
                `;

                container.appendChild(firework);

                setTimeout(() => {
                    firework.remove();
                }, 1500);
            }, i * 50);
        }
    };

    return (
        <div className="auth-page onboarding-page">
            <div className="firework-container"></div>
            <div className="gradient-orb"></div>
            <div className={step === "review" ? "onboarding-container" : "auth-container"}>
                <div
                    className={step === "review" ? "onboarding-card-wrapper" : "auth-card-wrapper"}
                    style={{
                        maxWidth: step !== "review" ? "480px" : undefined
                    }}
                >
                    <div className={step === "review" ? "onboarding-modal-card p-0 overflow-hidden animate-slide-up" : "auth-card p-0 overflow-hidden animate-slide-up"}>
                        {step !== "review" ? (
                            <div className="p-5">
                                <div className="auth-logo mb-4">
                                    <BrandLogo height={28} />
                                </div>

                                {step === "initial" && (
                                    <>
                                        <h2 className="form-title">Welcome to Meylo!</h2>
                                        <p className="form-subtitle">Enter your website to set up your company profile automatically.</p>

                                        {error && <div className="alert alert-danger">{error}</div>}

                                        <form onSubmit={handleWebsiteSubmit} className="mt-4">
                                            <div className="form-group mb-3">
                                                <input
                                                    type="url"
                                                    className="form-control form-control-lg"
                                                    placeholder="https://example.com"
                                                    value={websiteUrl}
                                                    onChange={(e) => setWebsiteUrl(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="d-grid gap-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-lg"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Analyzing...
                                                        </>
                                                    ) : (
                                                        "Continue"
                                                    )}
                                                </button>

                                                <div className="text-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-link text-muted text-decoration-none btn-sm"
                                                        onClick={() => setStep("prompt")}
                                                    >
                                                        No, I don't have a website
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </>
                                )}

                                {step === "prompt" && (
                                    <>
                                        <h2 className="form-title">Tell us about your business</h2>
                                        <p className="form-subtitle">Describe your company and what you do.</p>

                                        {error && <div className="alert alert-danger">{error}</div>}

                                        <form onSubmit={handlePromptSubmit} className="mt-4">
                                            <div className="form-group mb-3">
                                                <textarea
                                                    className="form-control form-control-lg"
                                                    rows={6}
                                                    placeholder="Tell us about your business. You can include your company name, industry sector, what services or products you offer, establishment date, and contact information..."
                                                    value={promptText}
                                                    onChange={(e) => setPromptText(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="d-grid gap-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-lg"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Analyzing...
                                                        </>
                                                    ) : (
                                                        "Continue"
                                                    )}
                                                </button>

                                                <div className="text-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-link text-muted text-decoration-none btn-sm"
                                                        onClick={() => setStep("initial")}
                                                    >
                                                        Back
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="d-flex flex-column flex-md-row h-100">
                                {/* Modern Sidebar */}
                                <div className="onboarding-sidebar" style={{ minWidth: "280px", maxWidth: "280px" }}>
                                    <div className="sidebar-header">
                                        <BrandLogo height={24} />
                                    </div>
                                    {renderSidebar()}
                                </div>

                                {/* Content */}
                                <div className="flex-grow-1 p-4 p-md-5">
                                    <h2 className="form-title mb-2">Review & Confirm</h2>
                                    <p className="form-subtitle mb-4">Please review your details before finishing.</p>

                                    {error && <div className="alert alert-danger mb-4">{error}</div>}

                                    <form onSubmit={handleFinalSubmit}>
                                        {activeSection === "company" && (
                                            <div className="animate-fade-in">
                                                <h5 className="mb-3 text-primary">
                                                    {localStorage.getItem("registration_company_type") === "individual"
                                                        ? t("general_information")
                                                        : t("company_information")}
                                                </h5>
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label">Company Name</label>
                                                        <input type="text" className="form-control" value={data.company.company_name || ""} onChange={(e) => handleInputChange("company", "company_name", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Company Type</label>
                                                        <select
                                                            className="form-select"
                                                            value={data.company.company_type || "single"}
                                                            onChange={(e) => handleInputChange("company", "company_type", e.target.value)}
                                                            disabled={true} // Disabled as it's determined by registration
                                                        >
                                                            <option value="single">Single Company</option>
                                                            <option value="agency">Agency</option>
                                                        </select>
                                                        <small className="text-muted">Determined by your account type</small>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Industry</label>
                                                        <input type="text" className="form-control" value={data.company.industry || ""} onChange={(e) => handleInputChange("company", "industry", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Establishment Date</label>
                                                        <input type="date" className="form-control" value={data.company.establishment_date || ""} onChange={(e) => handleInputChange("company", "establishment_date", e.target.value)} />
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label">Description</label>
                                                        <textarea className="form-control" rows={3} value={data.company.description || ""} onChange={(e) => handleInputChange("company", "description", e.target.value)} />
                                                    </div>
                                                    <div className="col-12">
                                                        <LogoUploadField
                                                            value={data.company.logo_url || ""}
                                                            onChange={(url) => handleInputChange("company", "logo_url", url)}
                                                            onTempPathChange={setTempLogoPath}
                                                            allowTempUpload={true}
                                                            label="Logo URL"
                                                            helperText="Upload a logo file or enter a URL"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-end mt-4">
                                                    <button type="button" className="btn btn-primary" onClick={() => setActiveSection("contact")} style={{ height: "42px" }}>
                                                        Next: Contact Details <i className="bi bi-arrow-right ms-2"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {activeSection === "contact" && (
                                            <div className="animate-fade-in">
                                                <h5 className="mb-3 text-primary">Contact Details</h5>
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label">Website</label>
                                                        <input type="url" className="form-control" value={data.company.website || ""} onChange={(e) => handleInputChange("company", "website", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Email Address</label>
                                                        <input type="email" className="form-control" value={data.company.email || ""} onChange={(e) => handleInputChange("company", "email", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Phone Number</label>
                                                        <input type="tel" className="form-control" value={data.company.phone || ""} onChange={(e) => handleInputChange("company", "phone", e.target.value)} />
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label">Address</label>
                                                        <input type="text" className="form-control" value={data.company.address || ""} onChange={(e) => handleInputChange("company", "address", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">City</label>
                                                        <input type="text" className="form-control" value={data.company.city || ""} onChange={(e) => handleInputChange("company", "city", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Country</label>
                                                        <input type="text" className="form-control" value={data.company.country || ""} onChange={(e) => handleInputChange("company", "country", e.target.value)} />
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-between mt-4">
                                                    <Button
                                                        variant="secondary"
                                                        className="px-4 d-flex align-items-center justify-content-center"
                                                        style={{ minWidth: "140px", height: "42px" }}
                                                        onClick={() => setActiveSection("company")}
                                                        leftIcon={<i className="bi bi-arrow-left"></i>}
                                                    >
                                                        Back
                                                    </Button>

                                                    <Button
                                                        type="submit"
                                                        variant="primary"
                                                        className="px-4 d-flex align-items-center justify-content-center"
                                                        style={{ minWidth: "200px", height: "42px", marginLeft: "10px" }}
                                                        isLoading={isLoading}
                                                        disabled={isLoading}
                                                    >
                                                        {isLoading ? "Setting up..." : "Complete Setup"}
                                                    </Button>
                                                </div>

                                            </div>
                                        )}


                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingPage;
